import React, { useState, useEffect, useRef } from 'react';
import { SimulationRequest, PortfolioRatio, RecurringIncome } from '../types';
import './SimulationForm.css';

interface SimulationFormProps {
  onSubmit: (request: SimulationRequest) => void;
  loading: boolean;
}

export const SimulationForm: React.FC<SimulationFormProps> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState<SimulationRequest>({
    withdrawalRate: 4, // Now percentage (4% instead of 0.04)
    startingBalance: 1000000,
    portfolioRatio: { equity: 0.75, bonds: 0.25 },
    recurringIncome: { socialSecurity: 30000, pension: 0, other: 0 },
    years: 30,
    runs: 8000,
    taxFree: false,
    useFlatAmount: false,
    flatWithdrawalAmount: 40000
  });

  // Add separate state for input display values to handle cursor positioning
  const [inputValues, setInputValues] = useState({
    startingBalance: '1000000',
    flatWithdrawalAmount: '40000',
    years: '30',
    socialSecurity: '30000',
    pension: '0',
    other: '0',
    runs: '8000'
  });

  const [portfolioPresets, setPortfolioPresets] = useState<any[]>([]);
  const [showAdvancedBonds, setShowAdvancedBonds] = useState(false);

  // Load portfolio presets on component mount
  useEffect(() => {
    fetch('/api/portfolio-presets')
      .then(res => res.json())
      .then(data => setPortfolioPresets(data.presets))
      .catch(err => console.error('Failed to load portfolio presets:', err));
  }, []);

  // Update input display values when formData changes
  useEffect(() => {
    setInputValues({
      startingBalance: formData.startingBalance.toString(),
      flatWithdrawalAmount: formData.flatWithdrawalAmount.toString(),
      years: formData.years.toString(),
      socialSecurity: formData.recurringIncome.socialSecurity.toString(),
      pension: formData.recurringIncome.pension.toString(),
      other: formData.recurringIncome.other.toString(),
      runs: formData.runs.toString()
    });
  }, [formData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInputDisplayChange = (field: string, displayValue: string) => {
    // Update the display value immediately for better UX
    setInputValues(prev => ({
      ...prev,
      [field]: displayValue
    }));

    // Convert to number and update form data
    const numericValue = displayValue === '' ? 0 : Number(displayValue);
    
    if (field === 'socialSecurity' || field === 'pension' || field === 'other') {
      setFormData(prev => ({
        ...prev,
        recurringIncome: {
          ...prev.recurringIncome,
          [field]: numericValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: numericValue
      }));
    }
  };

  const handlePortfolioChange = (field: 'equity' | 'bonds', value: number) => {
    const newPortfolio = { ...formData.portfolioRatio };
    newPortfolio[field] = value;
    
    // Ensure portfolio sums to 100%
    if (field === 'equity') {
      newPortfolio.bonds = Math.max(0, 1 - value);
    } else {
      newPortfolio.equity = Math.max(0, 1 - value);
    }
    
    setFormData(prev => ({
      ...prev,
      portfolioRatio: newPortfolio
    }));
  };

  const handleBondAllocationChange = (field: 'treasury' | 'aaa', value: number) => {
    const newBondAllocation = formData.portfolioRatio.bondAllocation 
      ? { ...formData.portfolioRatio.bondAllocation }
      : { treasury: 0.7, aaa: 0.3 };
    
    newBondAllocation[field] = value;
    
    // Only adjust the other slider if the total would exceed 100%
    const total = newBondAllocation.treasury + newBondAllocation.aaa;
    if (total > 1) {
      if (field === 'treasury') {
        newBondAllocation.aaa = 1 - value;
      } else {
        newBondAllocation.treasury = 1 - value;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      portfolioRatio: {
        ...prev.portfolioRatio,
        bondAllocation: newBondAllocation
      }
    }));
  };

  const handlePresetSelect = (preset: any) => {
    setFormData(prev => ({
      ...prev,
      portfolioRatio: { equity: preset.equity, bonds: preset.bonds }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If using flat amount, calculate the percentage
    let finalFormData = { ...formData };
    if (formData.useFlatAmount) {
      finalFormData.withdrawalRate = (formData.flatWithdrawalAmount / formData.startingBalance) * 100;
    }
    
    onSubmit(finalFormData);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <div className="simulation-form">
      <h2>📊 Simulation Parameters</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>💰 Portfolio & Withdrawal</h3>
          
          <div className="form-row">
            <label>
              Starting Balance:
              <input
                type="number"
                value={inputValues.startingBalance}
                onChange={(e) => handleInputDisplayChange('startingBalance', e.target.value)}
                onBlur={(e) => {
                  // Ensure we have a valid number on blur
                  const value = Number(e.target.value);
                  if (isNaN(value) || value < 10000) {
                    setInputValues(prev => ({ ...prev, startingBalance: '10000' }));
                    setFormData(prev => ({ ...prev, startingBalance: 10000 }));
                  }
                }}
                min="10000"
                step="10000"
                required
              />
              <span className="formatted-value">{formatCurrency(formData.startingBalance)}</span>
            </label>
          </div>

          <div className="form-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.useFlatAmount}
                onChange={(e) => handleInputChange('useFlatAmount', e.target.checked)}
              />
              <span className="checkbox-text">Use flat dollar amount instead of percentage</span>
            </label>
          </div>

          {!formData.useFlatAmount ? (
            <div className="form-row">
              <label>
                Initial Withdrawal Rate (Trinity Study Method):
                <div className="label-description">
                  Sets your first year expenses as a percentage of portfolio. This amount is then adjusted annually for inflation, not withdrawn as a percentage of remaining balance.
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.withdrawalRate}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    handleInputChange('withdrawalRate', value);
                  }}
                  required
                />
                <span className="formatted-value">{formData.withdrawalRate}%</span>
                <span className="formatted-value">({formatCurrency(formData.startingBalance * formData.withdrawalRate / 100)})</span>
              </label>
            </div>
          ) : (
            <div className="form-row">
              <label>
                Initial Withdrawal Amount (Trinity Study Method):
                <div className="label-description">
                  Sets your first year expenses as a flat dollar amount. This amount is then adjusted annually for inflation, not withdrawn as a percentage of remaining balance.
                </div>
                <input
                  type="number"
                  value={inputValues.flatWithdrawalAmount}
                  onChange={(e) => handleInputDisplayChange('flatWithdrawalAmount', e.target.value)}
                  onBlur={(e) => {
                    const value = Number(e.target.value);
                    if (isNaN(value) || value < 1000) {
                      setInputValues(prev => ({ ...prev, flatWithdrawalAmount: '1000' }));
                      setFormData(prev => ({ ...prev, flatWithdrawalAmount: 1000 }));
                    }
                  }}
                  min="1000"
                  step="1000"
                  required
                />
                <span className="formatted-value">{formatCurrency(formData.flatWithdrawalAmount)}</span>
                <span className="formatted-value">({((formData.flatWithdrawalAmount / formData.startingBalance) * 100).toFixed(1)}%)</span>
              </label>
            </div>
          )}

          <div className="form-row">
            <label>
              Retirement Years:
              <input
                type="number"
                value={inputValues.years}
                onChange={(e) => handleInputDisplayChange('years', e.target.value)}
                onBlur={(e) => {
                  const value = Number(e.target.value);
                  if (isNaN(value) || value < 10 || value > 50) {
                    setInputValues(prev => ({ ...prev, years: '30' }));
                    setFormData(prev => ({ ...prev, years: 30 }));
                  }
                }}
                min="10"
                max="50"
                step="1"
                required
              />
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>📈 Portfolio Allocation</h3>
          
          <div className="portfolio-presets">
            <h4>Quick Presets:</h4>
            <div className="preset-buttons">
              {portfolioPresets.map((preset, index) => (
                <button
                  key={index}
                  type="button"
                  className={`preset-btn ${formData.portfolioRatio.equity === preset.equity ? 'active' : ''}`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  <strong>{preset.name}</strong>
                  <span>{formatPercentage(preset.equity)} / {formatPercentage(preset.bonds)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <label>
              Equity Allocation:
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={formData.portfolioRatio.equity}
                onChange={(e) => handlePortfolioChange('equity', Number(e.target.value))}
              />
              <span className="formatted-value">{formatPercentage(formData.portfolioRatio.equity)}</span>
            </label>
          </div>

          <div className="form-row">
            <label>
              Bond Allocation:
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={formData.portfolioRatio.bonds}
                onChange={(e) => handlePortfolioChange('bonds', Number(e.target.value))}
              />
              <span className="formatted-value">{formatPercentage(formData.portfolioRatio.bonds)}</span>
            </label>
          </div>

          <div className="allocation-summary">
            <strong>Total: {formatPercentage(formData.portfolioRatio.equity + formData.portfolioRatio.bonds)}</strong>
          </div>

          {/* Advanced Bond Allocation */}
          {formData.portfolioRatio.bonds > 0 && (
            <div className="advanced-bonds-section">
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showAdvancedBonds}
                    onChange={(e) => setShowAdvancedBonds(e.target.checked)}
                  />
                  <span className="checkbox-text">Advanced Bond Allocation</span>
                </label>
              </div>

              {showAdvancedBonds && (
                <div className="bond-allocation-controls">
                  <div className="form-row">
                    <label>
                      Treasury Bonds (10-year):
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={formData.portfolioRatio.bondAllocation?.treasury || 0.7}
                        onChange={(e) => handleBondAllocationChange('treasury', Number(e.target.value))}
                      />
                      <span className="formatted-value">
                        {formatPercentage(formData.portfolioRatio.bondAllocation?.treasury || 0.7)}
                      </span>
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      Aaa Corporate Bonds:
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={formData.portfolioRatio.bondAllocation?.aaa || 0.3}
                        onChange={(e) => handleBondAllocationChange('aaa', Number(e.target.value))}
                      />
                      <span className="formatted-value">
                        {formatPercentage(formData.portfolioRatio.bondAllocation?.aaa || 0.3)}
                      </span>
                    </label>
                  </div>

                  <div className="bond-allocation-summary">
                    <strong>
                      Bond Allocation: {formatPercentage(formData.portfolioRatio.bondAllocation?.treasury || 0.7)} Treasury / {formatPercentage(formData.portfolioRatio.bondAllocation?.aaa || 0.3)} Aaa
                    </strong>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>💵 Recurring Income</h3>
          
          <div className="form-row">
            <label>
              Social Security (Annual):
              <input
                type="number"
                value={inputValues.socialSecurity}
                onChange={(e) => handleInputDisplayChange('socialSecurity', e.target.value)}
                onBlur={(e) => {
                  const value = Number(e.target.value);
                  if (isNaN(value) || value < 0) {
                    setInputValues(prev => ({ ...prev, socialSecurity: '0' }));
                    setFormData(prev => ({
                      ...prev,
                      recurringIncome: { ...prev.recurringIncome, socialSecurity: 0 }
                    }));
                  }
                }}
                min="0"
                step="1000"
              />
              <span className="formatted-value">{formatCurrency(formData.recurringIncome.socialSecurity)}</span>
            </label>
          </div>

          <div className="form-row">
            <label>
              Pension (Annual):
              <input
                type="number"
                value={inputValues.pension}
                onChange={(e) => handleInputDisplayChange('pension', e.target.value)}
                onBlur={(e) => {
                  const value = Number(e.target.value);
                  if (isNaN(value) || value < 0) {
                    setInputValues(prev => ({ ...prev, pension: '0' }));
                    setFormData(prev => ({
                      ...prev,
                      recurringIncome: { ...prev.recurringIncome, pension: 0 }
                    }));
                  }
                }}
                min="0"
                step="1000"
              />
              <span className="formatted-value">{formatCurrency(formData.recurringIncome.pension)}</span>
            </label>
          </div>

          <div className="form-row">
            <label>
              Other Income (Annual):
              <input
                type="number"
                value={inputValues.other}
                onChange={(e) => handleInputDisplayChange('other', e.target.value)}
                onBlur={(e) => {
                  const value = Number(e.target.value);
                  if (isNaN(value) || value < 0) {
                    setInputValues(prev => ({ ...prev, other: '0' }));
                    setFormData(prev => ({
                      ...prev,
                      recurringIncome: { ...prev.recurringIncome, other: 0 }
                    }));
                  }
                }}
                min="0"
                step="1000"
              />
              <span className="formatted-value">{formatCurrency(formData.recurringIncome.other)}</span>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>⚙️ Advanced Options</h3>
          
          <div className="form-row">
            <label>
              Monte Carlo Runs:
              <input
                type="number"
                value={inputValues.runs}
                onChange={(e) => handleInputDisplayChange('runs', e.target.value)}
                onBlur={(e) => {
                  const value = Number(e.target.value);
                  if (isNaN(value) || value < 1000 || value > 10000) {
                    const clampedValue = Math.min(Math.max(value, 1000), 10000);
                    setInputValues(prev => ({ ...prev, runs: clampedValue.toString() }));
                    setFormData(prev => ({ ...prev, runs: clampedValue }));
                  }
                }}
                min="1000"
                max="10000"
                step="1000"
              />
              <span className="formatted-value">{formData.runs?.toLocaleString()} simulations</span>
              <small className="form-help">Max 10,000 for performance</small>
            </label>
          </div>

          <div className="form-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.taxFree}
                onChange={(e) => handleInputChange('taxFree', e.target.checked)}
              />
              <span className="checkbox-text">Tax-Free Mode (skip tax calculations)</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? '🔄 Running Simulation...' : '🚀 Run Simulation'}
          </button>
        </div>
      </form>
    </div>
  );
};
